import {
    getDocs,
    collection,
    query,
    where,
    updateDoc,
    doc,
    getDoc,
    onSnapshot,
    orderBy,
    limit,
    startAfter,
    documentId,
    Timestamp,
} from "firebase/firestore";
import { db, auth } from '../../../../firebase/config';
import { fetchBranchBankId } from '../../../branches/service/branchesService';

export type NewLocationSurvey = {
    id: string;
    surveyId: string;
    createdBy: string;
    createdByName?: string;
    locationAddress: string;
    surveyRequestDate: string;
    surveyDate: string;
    surveySubmittedOn: string;
    spaceConsistsOf?: string;
    hazardousArea?: string;
    directions?: Record<string, any>;
    questions?: Record<string, any>;
    remarks?: string;
    createdAt?: any;
    assignedTo?: string;
    assignedToName?: string;
    assignedToCode?: string;
    assignedBy?: string;
    assignedByName?: string;
    bankId?: string | null;
    status?: string;
    isHazardous?: boolean;
    cancellationReason?: string;
    completedBy?: string;
    completedAt?: any;
};

type SurveysMap = Map<string, NewLocationSurvey>;

const sortSurveys = (surveys: NewLocationSurvey[]) => {
    return surveys.sort((a, b) => {
        const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
        const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
        return dateB.getTime() - dateA.getTime();
    });
};

export function subscribeToNewLocationSurveys(onUpdate: (surveys: NewLocationSurvey[]) => void): () => void {
    const uid = auth.currentUser?.uid;
    if (!uid) {
        console.log('No user ID found');
        return () => {};
    }

    let disposed = false;
    const unsubscribers: Array<() => void> = [];
    const surveysMap: SurveysMap = new Map();

    const emit = () => {
        if (disposed) return;
        onUpdate(sortSurveys(Array.from(surveysMap.values())));
    };

    const attachListener = (queryRef: any) => {
        const unsubscribe = onSnapshot(queryRef, (snapshot: any) => {
            snapshot.docChanges().forEach((change: any) => {
                const data = change.doc.data();

                if (change.type === 'removed') {
                    surveysMap.delete(change.doc.id);
                } else {
                    const questions = data.questions || {};
                    const hazardFromQuestions = questions.fuelStationAvailable === 'Yes' || questions.otherAssessmentHazardous === 'Yes';
                    const hazardFromDetails = typeof data.hazardousArea === 'string' && data.hazardousArea.trim() !== '';
                    const computedHazard = data.isHazardous ?? (hazardFromQuestions || hazardFromDetails);
                    surveysMap.set(change.doc.id, {
                        id: change.doc.id,
                        ...data,
                        isHazardous: computedHazard
                    } as NewLocationSurvey);
                }
            });

            emit();
        }, (error: any) => {
            console.error('Error listening to surveys:', error);
        });

        unsubscribers.push(unsubscribe);
    };

    (async () => {
        let bankId: string | null = null;
        try {
            bankId = await fetchBranchBankId(uid);
        } catch (error) {
            console.error('Error resolving bank ID for surveys:', error);
        }

        if (!bankId) {
            console.warn('Bank ID not found for user. Location survey subscription aborted.');
            return;
        }

        let designation = '';
        try {
            const userDoc = await getDoc(doc(db, 'banks', bankId, 'pos', uid));
            if (userDoc.exists()) {
                designation = (userDoc.data()?.designation || '').toString();
            }
        } catch (error) {
            console.error('Error fetching user designation for surveys:', error);
        }

        const normalizedDesignation = designation.replace(/_/g, ' ').toLowerCase();
        const isMIS = normalizedDesignation.includes('mis');
        const isRegional = normalizedDesignation.includes('regional pso');

        const surveysRef = collection(db, 'banks', bankId, 'locationSurveys');

        if (isMIS) {
            attachListener(surveysRef);
        } else {
            attachListener(query(surveysRef, where('createdBy', '==', uid)));
            attachListener(query(surveysRef, where('assignedTo', '==', uid)));

            if (isRegional && bankId) {
                try {
                    const managedPsosSnap = await getDocs(collection(db, 'banks', bankId, 'pos', uid, 'managedPsos'));
                    const managedIds = managedPsosSnap.docs
                        .map(docSnap => docSnap.id || (docSnap.data()?.psoId as string | undefined))
                        .filter((id): id is string => Boolean(id));

                    managedIds.forEach((managedId) => {
                        attachListener(query(surveysRef, where('createdBy', '==', managedId)));
                        attachListener(query(surveysRef, where('assignedTo', '==', managedId)));
                    });
                } catch (error) {
                    console.error('Error fetching managed PSO surveys:', error);
                }
            }
        }
    })();

    return () => {
        disposed = true;
        unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
}

export async function fetchNewLocationSurveys(): Promise<NewLocationSurvey[]> {
    const uid = auth.currentUser?.uid;
    if (!uid) {
        console.log('No user ID found');
        return [];
    }

    try {
        const surveysMap = new Map<string, NewLocationSurvey>();

        const addSnapshotToMap = (snapshot: any, bankFilter?: string | null) => {
            snapshot.forEach((docSnap: any) => {
                const data = docSnap.data();
                const docBankId = data.bankId ?? null;

                if (bankFilter && docBankId && docBankId !== bankFilter) {
                    return;
                }

                const questions = data.questions || {};
                const hazardFromQuestions = questions.fuelStationAvailable === 'Yes' || questions.otherAssessmentHazardous === 'Yes';
                const hazardFromDetails = typeof data.hazardousArea === 'string' && data.hazardousArea.trim() !== '';
                const computedHazard = data.isHazardous ?? (hazardFromQuestions || hazardFromDetails);

                surveysMap.set(docSnap.id, {
                    id: docSnap.id,
                    ...data,
                    isHazardous: computedHazard
                } as NewLocationSurvey);
            });
        };

        let bankId: string | null = null;
        try {
            bankId = await fetchBranchBankId(uid);
        } catch (error) {
            console.error('Error resolving bank ID for surveys:', error);
        }

        if (!bankId) {
            console.warn('Bank ID not found for user. Cannot fetch location surveys.');
            return [];
        }

        const surveysRef = collection(db, 'banks', bankId, 'locationSurveys');

        let designation = '';
        try {
            const userDoc = await getDoc(doc(db, 'banks', bankId, 'pos', uid));
            if (userDoc.exists()) {
                designation = (userDoc.data()?.designation || '').toString();
            }
        } catch (error) {
            console.error('Error fetching user designation for surveys:', error);
        }

        const normalizedDesignation = designation.replace(/_/g, ' ').toLowerCase();
        const isMIS = normalizedDesignation.includes('mis');
        const isRegional = normalizedDesignation.includes('regional pso');

        if (isMIS) {
            const allSnap = await getDocs(surveysRef);
            addSnapshotToMap(allSnap, bankId);
        } else {
            const createdSnap = await getDocs(query(surveysRef, where('createdBy', '==', uid)));
            addSnapshotToMap(createdSnap);

            const assignedSnap = await getDocs(query(surveysRef, where('assignedTo', '==', uid)));
            addSnapshotToMap(assignedSnap);

            if (isRegional && bankId) {
                try {
                    const managedPsosSnap = await getDocs(collection(db, 'banks', bankId, 'pos', uid, 'managedPsos'));
                    const managedIds = managedPsosSnap.docs
                        .map(docSnap => docSnap.id || (docSnap.data()?.psoId as string | undefined))
                        .filter((id): id is string => Boolean(id));

                    for (const managedId of managedIds) {
                        const managedCreated = await getDocs(query(surveysRef, where('createdBy', '==', managedId)));
                        addSnapshotToMap(managedCreated);

                        const managedAssigned = await getDocs(query(surveysRef, where('assignedTo', '==', managedId)));
                        addSnapshotToMap(managedAssigned);
                    }
                } catch (error) {
                    console.error('Error fetching managed PSO surveys:', error);
                }
            }
        }

        const surveysList: NewLocationSurvey[] = Array.from(surveysMap.values());

        console.log('Fetched New Location Surveys:', surveysList.length);

        return sortSurveys(surveysList);
    } catch (error) {
        console.error('Error fetching location surveys:', error);
        return [];
    }
}

export async function updateNewLocationSurvey(surveyId: string, bankId: string | null | undefined, updates: Partial<NewLocationSurvey>): Promise<void> {
    const uid = auth.currentUser?.uid;
    if (!uid) {
        throw new Error('User not authenticated');
    }

    if (!bankId) {
        throw new Error('Bank ID is required to update the survey');
    }

    try {
        const surveyRef = doc(db, 'banks', bankId, 'locationSurveys', surveyId);
        await updateDoc(surveyRef, {
            ...updates,
            updatedAt: new Date()
        });
    } catch (error) {
        console.error('Error updating location survey:', error);
        throw error;
    }
}

export type SurveyDateFilter = 'all' | 'daily' | 'weekly' | 'monthly';

export type LocationSurveyStatusFilter =
    | 'all'
    | 'assigned'
    | 'completed'
    | 'submitted'
    | 'approved'
    | 'rejected'
    | 'cancelled'
    | 'pending';

export type LocationSurveyHazardFilter = 'all' | 'yes' | 'no';

export function computeNewLocationSurveyStatusLabel(s: NewLocationSurvey): string {
    const raw = (s.status || '').trim();
    if (raw) return raw;
    if (s.assignedTo && s.assignedTo !== s.createdBy) return 'Assigned';
    return 'Completed';
}

function computeNewLocationSurveyHazardFlag(s: NewLocationSurvey): boolean {
    const surveyQuestions = (s.questions || {}) as Record<string, string>;
    const hazardFromQuestions =
        surveyQuestions.fuelStationAvailable === 'Yes' || surveyQuestions.otherAssessmentHazardous === 'Yes';
    const hazardFromDetails = typeof s.hazardousArea === 'string' && s.hazardousArea.trim() !== '';
    return s.isHazardous ?? (hazardFromQuestions || hazardFromDetails);
}

/** Normalize common date string formats to YYYY-MM-DD (request date, survey date, etc.). */
export function parseLocationSurveyDisplayYmd(raw: string | undefined): string | null {
    const t = (raw || '').trim();
    if (!t) return null;
    if (/^\d{4}-\d{2}-\d{2}/.test(t)) return t.slice(0, 10);
    const m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) {
        const d = m[1].padStart(2, '0');
        const mo = m[2].padStart(2, '0');
        const y = m[3];
        return `${y}-${mo}-${d}`;
    }
    const m2 = t.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (m2) {
        const d = m2[1].padStart(2, '0');
        const mo = m2[2].padStart(2, '0');
        const y = m2[3];
        return `${y}-${mo}-${d}`;
    }
    return null;
}

function mapDocToNewLocationSurvey(docSnap: { id: string; data: () => any }): NewLocationSurvey {
    const data = docSnap.data() as any;
    const questions = data.questions || {};
    const hazardFromQuestions = questions.fuelStationAvailable === 'Yes' || questions.otherAssessmentHazardous === 'Yes';
    const hazardFromDetails = typeof data.hazardousArea === 'string' && data.hazardousArea.trim() !== '';
    const computedHazard = data.isHazardous ?? (hazardFromQuestions || hazardFromDetails);

    return {
        id: docSnap.id,
        ...data,
        isHazardous: computedHazard,
    } as NewLocationSurvey;
}

export type LocationSurveyMatchOptions = {
    /** Case-insensitive substring on surveyId or locationAddress. */
    searchContains?: string;
    requestDateFromYmd?: string | null;
    requestDateToYmd?: string | null;
    surveyDateFromYmd?: string | null;
    surveyDateToYmd?: string | null;
    /** Exact match on `assignedToName` (case-insensitive); omit or `'all'` to skip. */
    assignedPsoName?: string | null;
    statusFilter?: LocationSurveyStatusFilter;
    hazardousFilter?: LocationSurveyHazardFilter;
};

export function newLocationSurveyMatchesClientFilters(s: NewLocationSurvey, m: LocationSurveyMatchOptions): boolean {
    const q = (m.searchContains || '').trim().toLowerCase();
    if (q) {
        const sid = (s.surveyId || '').toLowerCase();
        const addr = (s.locationAddress || '').toLowerCase();
        if (!sid.includes(q) && !addr.includes(q)) return false;
    }

    if (m.requestDateFromYmd && m.requestDateToYmd) {
        const ymd = parseLocationSurveyDisplayYmd(s.surveyRequestDate);
        if (!ymd || ymd < m.requestDateFromYmd || ymd > m.requestDateToYmd) return false;
    }

    if (m.surveyDateFromYmd && m.surveyDateToYmd) {
        const ymd = parseLocationSurveyDisplayYmd(s.surveyDate);
        if (!ymd || ymd < m.surveyDateFromYmd || ymd > m.surveyDateToYmd) return false;
    }

    const pso = (m.assignedPsoName || '').trim();
    if (pso && pso.toLowerCase() !== 'all') {
        const got = (s.assignedToName || '').trim().toLowerCase();
        if (got !== pso.toLowerCase()) return false;
    }

    const st = (m.statusFilter || 'all') as LocationSurveyStatusFilter;
    if (st !== 'all') {
        const label = computeNewLocationSurveyStatusLabel(s);
        const low = label.toLowerCase();
        if (st === 'pending') {
            const raw = (s.status || '').trim().toLowerCase();
            const isPendingLike =
                !raw ||
                raw === 'n/a' ||
                raw === 'na' ||
                raw === 'pending' ||
                raw === 'no status';
            if (!isPendingLike) return false;
        } else if (low !== st) {
            return false;
        }
    }

    const hz = m.hazardousFilter || 'all';
    if (hz !== 'all') {
        const flag = computeNewLocationSurveyHazardFlag(s);
        if (hz === 'yes' && !flag) return false;
        if (hz === 'no' && flag) return false;
    }

    return true;
}

/**
 * Loads location surveys in reverse createdAt order (no record-created UI filter) and applies
 * client-side match options — substring search, request/survey dates, PSO name, status, hazardous.
 */
export async function fetchNewLocationSurveysMatchingAll(params: {
    match: LocationSurveyMatchOptions;
    maxDocumentsToScan?: number;
    chunkSize?: number;
}): Promise<{
    surveys: NewLocationSurvey[];
    hitScanLimit: boolean;
    totalDocsRead: number;
    bankId: string | null;
}> {
    const uid = auth.currentUser?.uid;
    if (!uid) return { surveys: [], hitScanLimit: false, totalDocsRead: 0, bankId: null };

    const maxDocumentsToScan = Math.max(200, Math.min(8000, params.maxDocumentsToScan ?? 4000));
    const chunkSize = Math.max(50, Math.min(500, params.chunkSize ?? 400));

    const bankId = await fetchBranchBankId(uid);
    if (!bankId) return { surveys: [], hitScanLimit: false, totalDocsRead: 0, bankId: null };

    const surveysRef = collection(db, 'banks', bankId, 'locationSurveys');

    const matches: NewLocationSurvey[] = [];
    let totalDocsRead = 0;
    let lastCreatedAtMs: number | undefined;
    let lastId: string | undefined;

    while (totalDocsRead < maxDocumentsToScan) {
        const clauses: any[] = [];
        clauses.push(orderBy('createdAt', 'desc'));
        clauses.push(orderBy(documentId(), 'desc'));
        if (lastCreatedAtMs !== undefined && lastId !== undefined) {
            clauses.push(startAfter(Timestamp.fromMillis(lastCreatedAtMs), lastId));
        }
        const take = Math.min(chunkSize, maxDocumentsToScan - totalDocsRead);
        clauses.push(limit(take));

        const snap = await getDocs(query(surveysRef, ...clauses));
        if (snap.empty) break;

        for (const docSnap of snap.docs) {
            totalDocsRead++;
            const survey = mapDocToNewLocationSurvey(docSnap);
            if (newLocationSurveyMatchesClientFilters(survey, params.match)) {
                matches.push(survey);
            }
        }

        const lastDoc = snap.docs[snap.docs.length - 1];
        const lastData = (lastDoc?.data() as any) || {};
        const ms = lastData.createdAt?.toMillis?.();
        if (typeof ms === 'number') {
            lastCreatedAtMs = ms;
            lastId = lastDoc.id;
        } else {
            break;
        }

        if (snap.docs.length < take) break;
    }

    const hitScanLimit = totalDocsRead >= maxDocumentsToScan;
    return { surveys: matches, hitScanLimit, totalDocsRead, bankId };
}

export async function fetchNewLocationSurveyPsoNames(params?: {
    maxDocumentsToScan?: number;
    chunkSize?: number;
}): Promise<string[]> {
    const uid = auth.currentUser?.uid;
    if (!uid) return [];

    const maxDocumentsToScan = Math.max(200, Math.min(8000, params?.maxDocumentsToScan ?? 2500));
    const chunkSize = Math.max(50, Math.min(500, params?.chunkSize ?? 400));

    const bankId = await fetchBranchBankId(uid);
    if (!bankId) return [];

    const surveysRef = collection(db, 'banks', bankId, 'locationSurveys');
    const names = new Set<string>();
    let totalDocsRead = 0;
    let lastCreatedAtMs: number | undefined;
    let lastId: string | undefined;

    while (totalDocsRead < maxDocumentsToScan) {
        const clauses: any[] = [orderBy('createdAt', 'desc'), orderBy(documentId(), 'desc')];
        if (lastCreatedAtMs !== undefined && lastId !== undefined) {
            clauses.push(startAfter(Timestamp.fromMillis(lastCreatedAtMs), lastId));
        }
        const take = Math.min(chunkSize, maxDocumentsToScan - totalDocsRead);
        clauses.push(limit(take));

        const snap = await getDocs(query(surveysRef, ...clauses));
        if (snap.empty) break;

        for (const docSnap of snap.docs) {
            totalDocsRead++;
            const data = docSnap.data() as any;
            const n = String(data.assignedToName || '').trim();
            if (n) names.add(n);
        }

        const lastDoc = snap.docs[snap.docs.length - 1];
        const lastData = (lastDoc?.data() as any) || {};
        const ms = lastData.createdAt?.toMillis?.();
        if (typeof ms === 'number') {
            lastCreatedAtMs = ms;
            lastId = lastDoc.id;
        } else {
            break;
        }

        if (snap.docs.length < take) break;
    }

    return Array.from(names).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
}

export type NewLocationSurveyPageCursor = {
    createdAtMs?: number;
    surveyId?: string;
    id: string;
};

export async function fetchNewLocationSurveysPage(params: {
    pageSize: number;
    cursor?: NewLocationSurveyPageCursor | null;
}): Promise<{
    surveys: NewLocationSurvey[];
    cursor: NewLocationSurveyPageCursor | null;
    hasMore: boolean;
    bankId: string | null;
}> {
    const uid = auth.currentUser?.uid;
    if (!uid) return { surveys: [], cursor: null, hasMore: false, bankId: null };

    const pageSize = Math.max(1, Math.min(100, params.pageSize || 20));

    const bankId = await fetchBranchBankId(uid);
    if (!bankId) return { surveys: [], cursor: null, hasMore: false, bankId: null };

    const surveysRef = collection(db, 'banks', bankId, 'locationSurveys');

    const clauses: any[] = [];
    clauses.push(orderBy('createdAt', 'desc'));
    clauses.push(orderBy(documentId(), 'desc'));
    if (params.cursor?.createdAtMs) {
        clauses.push(startAfter(Timestamp.fromMillis(params.cursor.createdAtMs), params.cursor.id));
    }
    clauses.push(limit(pageSize + 1));
    const snap = await getDocs(query(surveysRef, ...clauses));

    const docs = snap.docs;
    const hasMore = docs.length > pageSize;
    const pageDocs = hasMore ? docs.slice(0, pageSize) : docs;

    const surveys = pageDocs.map((docSnap) => mapDocToNewLocationSurvey(docSnap));

    const lastDoc = pageDocs[pageDocs.length - 1];
    const lastData = (lastDoc?.data() as any) || {};
    const nextCursor = lastDoc
        ? lastData.createdAt?.toMillis
            ? { createdAtMs: lastData.createdAt.toMillis(), id: lastDoc.id }
            : null
        : null;

    return {
        surveys,
        cursor: nextCursor,
        hasMore,
        bankId,
    };
}

